import { isInterventionCancelledBeforeStart } from "../lib/interventionCancellation";
import { resolveHistoricalRevisionFollowUp } from "../lib/historicalRevisionFollowUp";
import { resolveInterventionStateFields, getInterventionDeliveryEndDates } from "../lib/interventionState.js";

export const CANONICAL_INTERVENTION_STATUSES = Object.freeze([
  "draft",
  "submitted",
  "in_review",
  "changes_requested",
  "approved",
  "rejected",
  "in_progress",
  "suspended",
  "completed",
  "cancelled",
]);

export const CANONICAL_INTERVENTION_REVIEW_STATUSES = Object.freeze([
  "draft",
  "submitted",
  "in_review",
  "changes_requested",
  "approved",
  "rejected",
]);

export const CANONICAL_INTERVENTION_DELIVERY_STATUSES = Object.freeze([
  "planned",
  "in_progress",
  "suspended",
  "completed",
  "cancelled",
]);

const CANONICAL_INTERVENTION_STATUS_SET = new Set(CANONICAL_INTERVENTION_STATUSES);
const CANONICAL_INTERVENTION_REVIEW_STATUS_SET = new Set(CANONICAL_INTERVENTION_REVIEW_STATUSES);
const CANONICAL_INTERVENTION_DELIVERY_STATUS_SET = new Set(CANONICAL_INTERVENTION_DELIVERY_STATUSES);

const normalizeWorkflowKey = value =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

const normalizePositiveInteger = value => {
  if (value === null || typeof value === "undefined" || value === "") return null;
  const numeric = Number(value);
  return Number.isInteger(numeric) && numeric > 0 ? numeric : null;
};

const parseMetadataObject = value => {
  if (!value) return {};
  if (typeof value === "object") return value;
  if (typeof value !== "string") return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const hasAppliedRevisionMetadata = metadata => {
  if (!metadata || typeof metadata !== "object") return false;
  if (metadata.lastAppliedRevision && typeof metadata.lastAppliedRevision === "object") return true;
  return Array.isArray(metadata.revisionHistory) && metadata.revisionHistory.length > 0;
};

const isManualBackloadMetadata = metadata => {
  if (!metadata || typeof metadata !== "object") return false;
  const source = normalizeWorkflowKey(metadata.source);
  const entryMode = normalizeWorkflowKey(metadata.entryMode ?? metadata.entry_mode);
  return source === "manual_backload" || source === "auto_assessment" || entryMode === "existing";
};

const resolveApprovalLetterFollowUpMetadata = metadata => {
  if (!metadata || typeof metadata !== "object") return {};
  const candidates = [
    metadata.approvalLetterFollowUp,
    metadata.approval_letter_follow_up,
    metadata.letterFollowUp,
    metadata.letter_follow_up,
  ];
  return candidates.find(candidate => candidate && typeof candidate === "object") || {};
};

export const INTERVENTION_PROPOSAL_STATUSES = new Set([
  "draft",
  "submitted",
  "in_review",
  "changes_requested",
]);

export const INTERVENTION_CLOSED_STATUSES = new Set(["completed", "cancelled"]);

export const INTERVENTION_OPEN_STATUSES = new Set([
  "draft",
  "submitted",
  "in_review",
  "changes_requested",
  "approved",
  "in_progress",
  "suspended",
]);

export const INTERVENTION_ACTIVATABLE_STATUSES = new Set(["approved"]);

export const INTERVENTION_CLOSABLE_STATUSES = new Set(["in_progress", "suspended"]);

// Submitted and finally decided packets are evidence records. Only an
// unsubmitted draft may be offered through the ordinary delete workflow.
export const INTERVENTION_DELETABLE_STATUSES = new Set(["draft"]);

export const normalizeInterventionStatus = (value, fallback = null) => {
  if (value === null || typeof value === "undefined") return fallback;
  const normalized = String(value)
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  return CANONICAL_INTERVENTION_STATUS_SET.has(normalized) ? normalized : fallback;
};

export const normalizeInterventionReviewStatus = (value, fallback = null) => {
  if (value === null || typeof value === "undefined") return fallback;
  const normalized = String(value)
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  return CANONICAL_INTERVENTION_REVIEW_STATUS_SET.has(normalized) ? normalized : fallback;
};

export const normalizeInterventionDeliveryStatus = (value, fallback = null) => {
  if (value === null || typeof value === "undefined") return fallback;
  const normalized = String(value)
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  return CANONICAL_INTERVENTION_DELIVERY_STATUS_SET.has(normalized) ? normalized : fallback;
};

export const isInterventionFinalDecisionRecorded = record => {
  if (!record || typeof record !== "object") return false;
  const workflow = record.reviewWorkflow || record.review_workflow || null;
  if (
    workflow &&
    typeof workflow === "object" &&
    normalizeWorkflowKey(
      workflow.currentStage ?? workflow.current_stage ?? null
    ) === "final_decision_recorded"
  ) {
    return true;
  }

  const proposalId = normalizePositiveInteger(
    record.proposalId ?? record.proposal_id ?? null
  );
  const proposalReviewStatus = normalizeInterventionReviewStatus(
    record.proposalReviewStatus ?? record.proposal_review_status ?? null,
    null
  );
  return Boolean(
    proposalId && ["approved", "rejected"].includes(proposalReviewStatus)
  );
};

export { resolveInterventionStateFields, getInterventionDeliveryEndDates };

export const formatInterventionStatusLabel = value => {
  const state = resolveInterventionStateFields(value);
  const normalized =
    state.effectiveStatus ||
    String(value || "")
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, "_");
  if (!normalized) return "-";
  if (normalized === "rejected") return "Denied";
  if (normalized === "changes_requested") return "Request Changes";
  return normalized
    .replace(/_/g, " ")
    .replace(/\b\w/g, char => char.toUpperCase());
};

export const isInterventionProposalStatus = status =>
  INTERVENTION_PROPOSAL_STATUSES.has(resolveInterventionStateFields(status).reviewStatus);

export const isInterventionClosedStatus = status =>
  INTERVENTION_CLOSED_STATUSES.has(
    resolveInterventionStateFields(status).deliveryStatus ||
      resolveInterventionStateFields(status).effectiveStatus
  );

export const isInterventionOpenStatus = status =>
  INTERVENTION_OPEN_STATUSES.has(resolveInterventionStateFields(status).effectiveStatus);

export const isInterventionActivatableStatus = status =>
  INTERVENTION_ACTIVATABLE_STATUSES.has(resolveInterventionStateFields(status).effectiveStatus);

export const isInterventionClosableStatus = status =>
  INTERVENTION_CLOSABLE_STATUSES.has(
    resolveInterventionStateFields(status).deliveryStatus ||
      resolveInterventionStateFields(status).effectiveStatus
  );

export const isInterventionDeletableStatus = status =>
  INTERVENTION_DELETABLE_STATUSES.has(resolveInterventionStateFields(status).effectiveStatus);

export const resolveInterventionApprovalLetterFollowUp = intervention => {
  if (!intervention || typeof intervention !== "object") {
    return { eligible: false, isRevision: false };
  }
  const state = resolveInterventionStateFields(intervention, { fallbackStatus: null });
  if (state.reviewStatus !== "approved" || isInterventionCancelledBeforeStart(intervention)) {
    return { eligible: false, isRevision: false };
  }

  const metadata = parseMetadataObject(intervention.metadata ?? intervention.metadata_json);
  const appliedRevision = hasAppliedRevisionMetadata(metadata);
  const letterFollowUp = resolveApprovalLetterFollowUpMetadata(metadata);
  const applied = metadata.lastAppliedRevision && typeof metadata.lastAppliedRevision === "object"
    ? metadata.lastAppliedRevision
    : {};
  const appliedRevisionDraftId = normalizePositiveInteger(
    applied.draftInterventionId ?? applied.draft_intervention_id
  );
  const followUpRevisionDraftId = normalizePositiveInteger(
    letterFollowUp.revisionDraftInterventionId ??
      letterFollowUp.revision_draft_intervention_id ??
      letterFollowUp.draftInterventionId ??
      letterFollowUp.draft_intervention_id
  );
  const followUpKind = normalizeWorkflowKey(
    letterFollowUp.kind ?? letterFollowUp.type ?? letterFollowUp.followUpType ?? letterFollowUp.follow_up_type
  );
  const followUpMatchesAppliedRevision =
    !appliedRevision ||
    followUpKind === "revision" ||
    Boolean(appliedRevisionDraftId && followUpRevisionDraftId === appliedRevisionDraftId);
  const letterSentAt =
    followUpMatchesAppliedRevision
      ? (
          letterFollowUp.approvalLetterSentAt ||
          letterFollowUp.approval_letter_sent_at ||
          letterFollowUp.sentAt ||
          letterFollowUp.sent_at ||
          letterFollowUp.completedAt ||
          letterFollowUp.completed_at ||
          null
        )
      : null;
  const letterSent = followUpMatchesAppliedRevision && (
    Boolean(letterSentAt) ||
    normalizeWorkflowKey(letterFollowUp.status) === "sent" ||
    letterFollowUp.completed === true
  );
  const proposalId = normalizePositiveInteger(intervention.proposalId ?? intervention.proposal_id);
  const proposalReviewStatus = normalizeInterventionReviewStatus(
    intervention.proposalReviewStatus ?? intervention.proposal_review_status,
    null
  );
  const proposalKind = normalizeWorkflowKey(intervention.proposalKind ?? intervention.proposal_kind);
  const approvedProposalLinked =
    proposalId &&
    proposalReviewStatus === "approved" &&
    !isManualBackloadMetadata(metadata);

  if (!appliedRevision && !approvedProposalLinked) {
    return { eligible: false, isRevision: false };
  }

  const historicalResolution = appliedRevision ? resolveHistoricalRevisionFollowUp(intervention) : null;
  const isRevision = appliedRevision || proposalKind === "revision";
  const title =
    applied.sourceTitle ||
    metadata.title ||
    intervention.title ||
    (intervention.code ? `Intervention ${intervention.code}` : "this approved intervention");

  return {
    eligible: true,
    isRevision,
    letterSent,
    letterSentAt,
    pendingLetter: !letterSent && !historicalResolution,
    historicalResolution,
    sourceTitle: title,
    sourceInterventionId: intervention.id || intervention.intervention_id || null,
    sourceActionPlanId: intervention.actionPlanId || intervention.action_plan_id || null,
    sourceStatus: state.effectiveStatus || "approved",
  };
};

export const isInterventionPlanRetired = plan =>
  Boolean(plan?.archivedAt || plan?.archived_at) ||
  ["archived", "closed"].includes(normalizeWorkflowKey(plan?.status));

export const isInterventionApprovalLetterFollowUpEligible = intervention =>
  resolveInterventionApprovalLetterFollowUp(intervention).eligible;

export const isInterventionApprovalLetterFollowUpPending = intervention => {
  const followUp = resolveInterventionApprovalLetterFollowUp(intervention);
  return Boolean(followUp.eligible && followUp.pendingLetter);
};
